import { ReactNode } from 'react';
import { AlertDialog as RxAlertDialog, VisuallyHidden } from 'radix-ui';
import { useTranslation } from 'react-i18next';
import { withStyles } from 'vopay';

import styles from './AlertDialog.module.scss';

import { Button, CircleButton, Title } from '#shared/components/UI';

const cn = withStyles(styles);

export type AlertDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onAbort?: () => void;
  onConfirm?: () => void;
  title?: ReactNode;
  content?: ReactNode;
  abortLabel?: ReactNode;
  confirmLabel?: ReactNode;
  top?: ReactNode;
  bottom?: ReactNode;
  closeButton?: boolean;
  /**
   * Applied to the dialog card. Set any of `--VP__AlertDialog__padding` / `__width` / `__minWidth` /
   * `__maxWidth` / `__topSpacing` / `__infoSpacing` on it to tune the layout.
   */
  className?: string;
  confirmColorScheme?: 'primary' | 'destructive';
};

export default function AlertDialog({
  isOpen,
  title,
  content,
  onClose,
  onAbort,
  onConfirm,
  abortLabel,
  confirmLabel,
  top,
  bottom,
  closeButton = false,
  className,
  confirmColorScheme = 'primary',
}: AlertDialogProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.AlertDialog' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });

  return (
    <RxAlertDialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <RxAlertDialog.Overlay data-testid="alert-dialog-overlay" className={cn('alertDialogOverlay')} />

      {/* Radix needs a Title for the accessible name and a Description (or an explicit
          aria-describedby={undefined}) — both props are optional here, so cover the missing cases. */}
      <RxAlertDialog.Content
        data-testid="alert-dialog"
        className={cn('alertDialogContainer', className)}
        {...(content ? {} : { 'aria-describedby': undefined })}
      >
        {closeButton && (
          <RxAlertDialog.Cancel asChild>
            <CircleButton
              data-testid="alert-dialog-close"
              aria-label={tButtons('close', { defaultValue: 'Close' }) ?? undefined}
              className={cn('closeButton')}
              icon="close"
              circleFill={false}
            />
          </RxAlertDialog.Cancel>
        )}

        <div className={cn('top')}>
          {top}

          <div className={cn('info')}>
            {title ? (
              <RxAlertDialog.Title asChild>
                <Title level="3">{title}</Title>
              </RxAlertDialog.Title>
            ) : (
              <VisuallyHidden.Root asChild>
                <RxAlertDialog.Title>{t('fallbackTitle', { defaultValue: 'Alert' })}</RxAlertDialog.Title>
              </VisuallyHidden.Root>
            )}

            {content && (
              <RxAlertDialog.Description asChild>
                <div className={cn('description')}>{content}</div>
              </RxAlertDialog.Description>
            )}
          </div>
        </div>

        {abortLabel || confirmLabel ? (
          <div className={cn('controls')}>
            {abortLabel && (
              <RxAlertDialog.Cancel asChild onClick={onAbort}>
                <Button colorScheme="secondary">{abortLabel}</Button>
              </RxAlertDialog.Cancel>
            )}

            {confirmLabel && (
              <RxAlertDialog.Action asChild onClick={onConfirm}>
                <Button colorScheme={confirmColorScheme}>{confirmLabel}</Button>
              </RxAlertDialog.Action>
            )}
          </div>
        ) : (
          /* A dialogue with no action buttons still needs a way out. */
          <RxAlertDialog.Cancel asChild>
            <Button colorScheme="secondary">{tButtons('close', { defaultValue: 'Close' })}</Button>
          </RxAlertDialog.Cancel>
        )}

        {bottom && <div className={cn('bottom')}>{bottom}</div>}
      </RxAlertDialog.Content>
    </RxAlertDialog.Root>
  );
}
